import { z } from 'zod';
import { MaterialKindSchema } from '@shared/spec';
import type { IntakeSpec } from '@shared/types';
import i18n from '@/i18n/i18n';

// ─── Core ─────────────────────────────────────────────────────────────────

export const IntakeCoreSchema = z.object({
  intendedUse: z
    .string()
    .min(1, { error: () => i18n.t('services.intendedUseRequired', { ns: 'common' }) })
    .max(120),
  size: z
    .object({
      length: z.number().positive().optional(),
      width: z.number().positive().optional(),
      height: z.number().positive().optional(),
    })
    .optional(),
  primaryMaterial: MaterialKindSchema.optional(),
  fastenerPreference: z
    .enum(['screws', 'hidden', 'glue_only', 'mortise_tenon', 'none', 'mixed'])
    .optional(),
});

export const DrawingPreferencesSchema = z.object({
  scope: z.enum(['assembly_only', 'parts_only', 'both']),
  sectionCuts: z.enum(['auto', 'none']),
  showHoleTable: z.boolean(),
  showBOM: z.boolean(),
  toleranceClass: z.enum(['loose', 'standard', 'tight', 'precision']),
  jisStrict: z.boolean(),
});

// ─── 21 sub-type detail variants ──────────────────────────────────────────

const FurnitureTableSchema = z.object({
  category: z.literal('furniture'),
  subType: z.literal('table'),
  numSeats: z.union([z.literal(2), z.literal(4), z.literal(6), z.literal(8), z.literal(10), z.literal(12)]),
  shape: z.enum(['rectangular', 'round', 'oval', 'square']),
  legStyle: z.enum(['straight', 'tapered', 'turned', 'trestle', 'pedestal']),
  joineryType: z.enum(['mortise_tenon', 'dowel', 'biscuit', 'pocket_screw', 'metal_bracket']),
  finish: z.enum(['oil', 'lacquer', 'paint', 'wax', 'unfinished']),
  hasStretcher: z.boolean(),
  hasApron: z.boolean(),
});

const FurnitureChairSchema = z.object({
  category: z.literal('furniture'),
  subType: z.literal('chair'),
  chairType: z.enum(['dining', 'lounge', 'office', 'stool', 'rocking']),
  backStyle: z.enum(['spindle', 'slat', 'panel', 'upholstered', 'mesh']),
  hasArmrests: z.boolean(),
  legStyle: z.enum(['straight', 'tapered', 'splayed', 'cabriole']),
  joineryType: z.enum(['mortise_tenon', 'dowel', 'screw', 'mixed']),
  finish: z.enum(['oil', 'lacquer', 'paint', 'wax', 'unfinished']),
});

const FurnitureShelfSchema = z.object({
  category: z.literal('furniture'),
  subType: z.literal('shelf'),
  shelfType: z.enum(['bookcase', 'wall_mounted', 'cube', 'ladder', 'corner']),
  shelfCount: z.number().int().min(2).max(12),
  adjustableShelves: z.boolean(),
  hasBack: z.boolean(),
  joineryType: z.enum(['dado', 'dowel', 'screw', 'biscuit', 'cam_lock']),
  finish: z.enum(['oil', 'lacquer', 'paint', 'wax', 'unfinished']),
});

const FurnitureBedSchema = z.object({
  category: z.literal('furniture'),
  subType: z.literal('bed'),
  bedSize: z.enum(['single', 'semi_double', 'double', 'queen', 'king']),
  hasHeadboard: z.boolean(),
  hasFootboard: z.boolean(),
  hasUnderStorage: z.boolean(),
  slatCount: z.number().int().min(8).max(40),
  joineryType: z.enum(['mortise_tenon', 'bed_bolt', 'cam_lock', 'screw']),
  finish: z.enum(['oil', 'lacquer', 'paint', 'wax', 'unfinished']),
});

const FurnitureDeskSchema = z.object({
  category: z.literal('furniture'),
  subType: z.literal('desk'),
  deskType: z.enum(['simple', 'pedestal_single', 'pedestal_double', 'l_shape', 'standing']),
  drawerCount: z.number().int().min(0).max(6),
  hasCableManagement: z.boolean(),
  legStyle: z.enum(['straight', 'tapered', 'panel', 'metal']),
  joineryType: z.enum(['mortise_tenon', 'dowel', 'pocket_screw', 'cam_lock']),
  finish: z.enum(['oil', 'lacquer', 'paint', 'wax', 'unfinished']),
});

const FurnitureCabinetSchema = z.object({
  category: z.literal('furniture'),
  subType: z.literal('cabinet'),
  cabinetType: z.enum(['base', 'wall', 'tall', 'corner', 'island']),
  doorStyle: z.enum(['flat_panel', 'shaker', 'raised_panel', 'glass', 'sliding', 'none']),
  doorCount: z.number().int().min(0).max(6),
  drawerCount: z.number().int().min(0).max(8),
  shelvesInside: z.number().int().min(0).max(6),
  hardwareStyle: z.enum(['concealed_hinge', 'butt_hinge', 'piano_hinge', 'cup_hinge']),
  joineryType: z.enum(['dado', 'dowel', 'cam_lock', 'pocket_screw']),
  finish: z.enum(['oil', 'lacquer', 'paint', 'melamine', 'unfinished']),
});

const MechanicalBracketSchema = z.object({
  category: z.literal('mechanical'),
  subType: z.literal('bracket'),
  bracketType: z.enum(['L', 'T', 'flat', 'angle', 'gusset']),
  mountingPattern: z.enum(['corner', 'flat', 'angled', 'offset']),
  holeCount: z.number().int().min(2).max(20),
  holeSize: z.enum(['M3', 'M4', 'M5', 'M6', 'M8', 'M10']),
  holeFinish: z.enum(['through', 'countersunk', 'counterbored', 'threaded']),
  loadDirection: z.enum(['shear', 'tension', 'cantilever', 'mixed']),
  surfaceFinish: z.enum(['as_machined', 'powder_coat', 'anodized', 'plated', 'painted']),
});

const MechanicalHousingSchema = z.object({
  category: z.literal('mechanical'),
  subType: z.literal('housing'),
  housingType: z.enum(['enclosure', 'gearbox', 'motor', 'pump', 'electronics_box']),
  closure: z.enum(['lid_screwed', 'lid_snap_fit', 'split_clamshell', 'access_panel']),
  ipRating: z.enum(['none', 'IP54', 'IP65', 'IP67']),
  internalFeatures: z.array(z.enum(['mounting_bosses', 'cable_glands', 'vent', 'cooling_fins'])),
  fastenerSize: z.enum(['M2', 'M2.5', 'M3', 'M4', 'M5']),
  surfaceFinish: z.enum(['as_machined', 'powder_coat', 'anodized', 'painted']),
});

const MechanicalGearSchema = z.object({
  category: z.literal('mechanical'),
  subType: z.literal('gear'),
  gearType: z.enum(['spur', 'helical', 'bevel', 'worm', 'rack']),
  module: z.number().min(0.5).max(5),
  toothCount: z.number().int().min(8).max(120),
  pressureAngle: z.union([z.literal(14.5), z.literal(20), z.literal(25)]),
  faceWidth: z.number().positive(),
  boreDiameter: z.number().min(0),
  hubStyle: z.enum(['none', 'plain', 'set_screw', 'keyed', 'press_fit']),
});

const MechanicalShaftSchema = z.object({
  category: z.literal('mechanical'),
  subType: z.literal('shaft'),
  shaftType: z.enum(['plain', 'stepped', 'splined', 'threaded', 'keyed']),
  diameter: z.number().positive(),
  keywayPresent: z.boolean(),
  bearingFit: z.enum(['none', 'press', 'sliding', 'snug', 'transition']),
  endStyle: z.enum(['square', 'chamfered', 'tapered', 'threaded', 'splined']),
  surfaceFinish: z.enum(['as_machined', 'ground', 'polished', 'plated']),
});

const MechanicalFastenerPartSchema = z.object({
  category: z.literal('mechanical'),
  subType: z.literal('fastener_part'),
  fastenerType: z.enum(['screw', 'bolt', 'nut', 'washer', 'stud', 'rivet']),
  size: z.enum(['M2', 'M3', 'M4', 'M5', 'M6', 'M8', 'M10', 'M12', 'M16']),
  length: z.number().positive(),
  head: z.enum(['flat', 'pan', 'socket', 'hex', 'button', 'cheese', 'countersunk']),
  thread: z.enum(['full', 'partial', 'none']),
  finish: z.enum(['plain', 'zinc', 'black_oxide', 'stainless', 'titanium']),
});

const MechanicalFixtureSchema = z.object({
  category: z.literal('mechanical'),
  subType: z.literal('fixture'),
  fixtureType: z.enum(['jig', 'clamp', 'guide', 'stop', 'workholding']),
  workpieceMax: z.object({
    length: z.number().positive(),
    width: z.number().positive(),
    height: z.number().positive(),
  }),
  clampingMethod: z.enum(['toggle', 'cam', 'screw', 'pneumatic', 'magnetic']),
  baseplateMounting: z.enum(['tslot', 'magnetic', 'screw_down', 'free_standing']),
  toleranceClass: z.enum(['loose', 'standard', 'tight', 'precision']),
  hasAlignmentPins: z.boolean(),
});

const ArchitectureBuildingSchema = z.object({
  category: z.literal('architecture'),
  subType: z.literal('building'),
  buildingType: z.enum(['house', 'apartment', 'office', 'retail', 'warehouse', 'mixed_use']),
  scaleRatio: z.enum(['1:50', '1:100', '1:200', '1:500', '1:1000']),
  floorCount: z.number().int().min(1).max(30),
  roofStyle: z.enum(['flat', 'gable', 'hip', 'shed', 'mansard', 'butterfly']),
  showInterior: z.boolean(),
  showLandscape: z.boolean(),
});

const ArchitectureInteriorRoomSchema = z.object({
  category: z.literal('architecture'),
  subType: z.literal('interior_room'),
  roomType: z.enum(['living', 'bedroom', 'kitchen', 'bath', 'office', 'retail']),
  scaleRatio: z.enum(['1:20', '1:50', '1:100']),
  ceilingHeight: z.number().positive(),
  showFurniture: z.boolean(),
  wallTreatment: z.enum(['plain', 'wallpaper', 'paint', 'paneling', 'tile']),
  showCeiling: z.boolean(),
});

const ArchitectureSiteSchema = z.object({
  category: z.literal('architecture'),
  subType: z.literal('site'),
  siteType: z.enum(['urban', 'suburban', 'rural', 'park', 'campus']),
  scaleRatio: z.enum(['1:200', '1:500', '1:1000', '1:2000']),
  showTopography: z.boolean(),
  showVegetation: z.boolean(),
  showRoads: z.boolean(),
  buildingCount: z.number().int().min(0).max(50),
});

const ArchitectureFacadeDetailSchema = z.object({
  category: z.literal('architecture'),
  subType: z.literal('facade_detail'),
  detailType: z.enum(['window', 'door', 'cornice', 'parapet', 'curtain_wall']),
  scaleRatio: z.enum(['1:5', '1:10', '1:20', '1:50']),
  material: z.enum(['masonry', 'concrete', 'metal', 'glass', 'wood', 'composite']),
  showSection: z.boolean(),
  showLayers: z.boolean(),
});

const PrintingDecorativeSchema = z.object({
  category: z.literal('printing'),
  subType: z.literal('decorative'),
  itemType: z.enum(['vase', 'planter', 'art_object', 'ornament']),
  printer: z.enum(['FDM', 'SLA', 'SLS']),
  layerHeight: z.number().min(0.05).max(0.4),
  shellThickness: z.number().min(0.8).max(5),
  supportPolicy: z.enum(['avoid', 'allow_minimal', 'allow_full']),
  vaseModeCompatible: z.boolean(),
});

const PrintingFunctionalSchema = z.object({
  category: z.literal('printing'),
  subType: z.literal('functional'),
  functionalRole: z.enum(['mount', 'adapter', 'tool', 'hook', 'bracket', 'gear']),
  printer: z.enum(['FDM', 'SLA', 'SLS']),
  infillPercent: z.number().min(0).max(100),
  shellThickness: z.number().min(0.8).max(5),
  supportPolicy: z.enum(['avoid', 'allow_minimal', 'allow_full']),
  toleranceClass: z.enum(['loose', 'standard', 'tight']),
  insertType: z.enum(['none', 'heat_set', 'press_fit', 'self_tapping']),
});

const PrintingReplacementSchema = z.object({
  category: z.literal('printing'),
  subType: z.literal('replacement'),
  replacingType: z.enum(['knob', 'clip', 'gear', 'bracket', 'cover', 'connector']),
  printer: z.enum(['FDM', 'SLA']),
  matchOriginalDimensions: z.boolean(),
  toleranceClass: z.enum(['loose', 'standard', 'tight']),
  fastenerInterface: z.enum(['none', 'screw_M3', 'screw_M4', 'screw_M5', 'snap_fit']),
  reinforcement: z.enum(['none', 'thicker_walls', 'ribs', 'metal_insert']),
});

const PrintingMiniatureSchema = z.object({
  category: z.literal('printing'),
  subType: z.literal('miniature'),
  miniType: z.enum(['figure', 'terrain', 'vehicle', 'building_kit', 'modular_tile']),
  scale: z.enum(['1:35', '1:48', '1:72', '1:100', '28mm', '32mm']),
  printer: z.enum(['FDM', 'SLA']),
  layerHeight: z.number().min(0.05).max(0.4),
  detailLevel: z.enum(['low', 'medium', 'high', 'ultra']),
  basePresent: z.boolean(),
});

const PrintingEnclosureSchema = z.object({
  category: z.literal('printing'),
  subType: z.literal('enclosure'),
  enclosureFor: z.enum(['electronics', 'storage', 'food_safe', 'outdoor', 'general']),
  printer: z.enum(['FDM', 'SLA']),
  closure: z.enum(['snap_fit', 'screw', 'magnet', 'press_fit', 'hinged_lid']),
  insertType: z.enum(['none', 'heat_set_M2', 'heat_set_M3', 'heat_set_M4']),
  hasVents: z.boolean(),
  hasCableEntry: z.boolean(),
  ipRating: z.enum(['none', 'IP54', 'IP65']),
});

export const IntakeDetailsSchema = z.discriminatedUnion('subType', [
  FurnitureTableSchema, FurnitureChairSchema, FurnitureShelfSchema,
  FurnitureBedSchema, FurnitureDeskSchema, FurnitureCabinetSchema,
  MechanicalBracketSchema, MechanicalHousingSchema, MechanicalGearSchema,
  MechanicalShaftSchema, MechanicalFastenerPartSchema, MechanicalFixtureSchema,
  ArchitectureBuildingSchema, ArchitectureInteriorRoomSchema,
  ArchitectureSiteSchema, ArchitectureFacadeDetailSchema,
  PrintingDecorativeSchema, PrintingFunctionalSchema, PrintingReplacementSchema,
  PrintingMiniatureSchema, PrintingEnclosureSchema,
]);

// ─── Top-level IntakeSpec with cross-field validation ─────────────────────

export const IntakeSpecSchema: z.ZodType<IntakeSpec> = z
  .object({
    category: z.enum(['furniture', 'mechanical', 'architecture', 'printing']),
    subType: z.string(), // narrowed by IntakeDetailsSchema below
    qualityLevel: z.enum(['draft', 'standard', 'manufacturing', 'extreme']),
    core: IntakeCoreSchema,
    drawing: DrawingPreferencesSchema,
    details: IntakeDetailsSchema,
    freeform: z.string().max(1000),
    submittedAt: z.string().datetime(),
  })
  .refine(
    (s) => s.details.category === s.category && s.details.subType === s.subType,
    {
      error: () => i18n.t('services.intakeMismatch', { ns: 'common' }),
      path: ['details'],
    },
  ) as z.ZodType<IntakeSpec>;
